/*
 * Quick Shop (HAB-18)
 *
 * <quick-shop>        one per product card, rendered by snippets/quick-shop.liquid
 * <quick-shop-sheet>  one per page, rendered by snippets/quick-shop-sheet.liquid
 *
 * Size chips are rendered server-side and carry their own variant id, so adding
 * to cart is a single POST with no lookup first.
 *
 * This deliberately does not use <product-form>: product-form.js finishes with
 * cart.renderContents(), and CartDrawer.renderContents() ends by calling
 * this.open(). The Figma flow keeps the customer in the grid, so the cart
 * sections are swapped here by hand and the drawer is left closed.
 */

(function () {
	const SECTION_CART_DRAWER = 'cart-drawer';
	const SECTION_CART_BUBBLE = 'cart-icon-bubble';

	/* Fallback for browsers that skip transitionend (reduced motion, hidden tab). */
	const TRANSITION_FALLBACK = 400;

	/*
	 * Binds an activation handler for mouse, keyboard and touch.
	 *
	 * The touchend listener calls preventDefault, which stops the browser from
	 * synthesizing the follow-up click. That synthesized click is resolved
	 * against the touch coordinates, so on an overlay that is still animating it
	 * can be delivered somewhere other than the element the finger was on -
	 * which is how a tap on a size chip ends up navigating to the product page.
	 * Suppressing it means one activation per tap, on the element that was hit.
	 */
	function bindActivation(element, handler) {
		if (!element) return;

		element.addEventListener('click', handler);
		element.addEventListener(
			'touchend',
			(event) => {
				event.preventDefault();
				handler(event);
			},
			{ passive: false }
		);
	}

	/* Runs `callback` when `element`'s transition finishes, or after a timeout. */
	function afterTransition(element, callback) {
		if (!element) {
			callback();
			return;
		}

		let done = false;
		const finish = () => {
			if (done) return;
			done = true;
			element.removeEventListener('transitionend', onEnd);
			callback();
		};
		const onEnd = (event) => {
			if (event.target === element) finish();
		};

		element.addEventListener('transitionend', onEnd);
		setTimeout(finish, TRANSITION_FALLBACK);
	}

	function getCartDrawer() {
		return document.querySelector('cart-drawer');
	}

	function getSectionIds(cart) {
		if (cart) return cart.getSectionsToRender().map((section) => section.id);
		return [SECTION_CART_BUBBLE];
	}

	function sectionInnerHTML(html, selector) {
		const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
		return parsed ? parsed.innerHTML : null;
	}

	/*
	 * The replacement half of CartDrawer.renderContents(), without the open()
	 * call at the end of it.
	 */
	function refreshCartSections(sections) {
		if (!sections) return;

		const drawerContents = document.querySelector('#CartDrawer');
		if (drawerContents && sections[SECTION_CART_DRAWER]) {
			const html = sectionInnerHTML(sections[SECTION_CART_DRAWER], '#CartDrawer');
			if (html !== null) {
				drawerContents.classList.remove('is-empty');
				drawerContents.innerHTML = html;

				/*
				 * renderContents() rebinds this listener after every swap; the
				 * overlay node we just replaced took the old one with it.
				 */
				const cart = getCartDrawer();
				const overlay = document.querySelector('#CartDrawer-Overlay');
				if (cart && overlay) overlay.addEventListener('click', cart.close.bind(cart));
			}
		}

		const bubble = document.getElementById(SECTION_CART_BUBBLE);
		if (bubble && sections[SECTION_CART_BUBBLE]) {
			const html = sectionInnerHTML(sections[SECTION_CART_BUBBLE], '.shopify-section');
			if (html !== null) bubble.innerHTML = html;
		}
	}

	if (!customElements.get('quick-shop-sheet')) {
		customElements.define(
			'quick-shop-sheet',
			class QuickShopSheet extends HTMLElement {
				constructor() {
					super();

					this.content = this.querySelector('.quick-shop-sheet__content');
					this.sizes = this.querySelector('.quick-shop-sheet__sizes');
					this.errorElement = this.querySelector('.quick-shop-sheet__error');
					this.quickShop = null;

					bindActivation(this.querySelector('.quick-shop-sheet__overlay'), () => this.close());
					bindActivation(this.querySelector('.quick-shop-sheet__close'), () => this.close());

					this.addEventListener('keyup', (event) => {
						if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close();
					});

					if (this.sizes) {
						bindActivation(this.sizes, (event) => {
							/* Ignored until the slide-in has finished - see [data-ready] in the CSS. */
							if (this.dataset.ready !== 'true') return;

							const chip = event.target.closest('.quick-shop__size');
							if (!chip || chip.disabled || !this.quickShop) return;
							this.quickShop.addVariant(chip.dataset.variantId, { chip: chip, sheet: this });
						});
					}
				}

				/*
				 * Reparented to <body> so no ancestor can clip it, give it a stacking
				 * context, or become the containing block for its fixed positioning.
				 * Same reason ModalDialog does this in global.js.
				 */
				connectedCallback() {
					if (this.moved) return;
					this.moved = true;
					document.body.appendChild(this);
				}

				open(quickShop) {
					this.quickShop = quickShop;
					if (this.sizes) this.sizes.innerHTML = quickShop.sizesHTML;
					this.hideError();

					const title = quickShop.dataset.productTitle;
					const template = this.dataset.labelTemplate;
					if (this.content && title && template) {
						this.content.setAttribute('aria-label', template.replace('[product]', title));
					}

					this.openedBy = quickShop.addButton || quickShop.trigger || null;
					this.setAttribute('open', '');
					document.body.classList.add('overflow-hidden');

					/*
					 * [open] switches display, so the slide-in needs a frame at the closed
					 * transform before [data-visible] moves it - otherwise it jumps.
					 */
					requestAnimationFrame(() => {
						requestAnimationFrame(() => {
							if (!this.hasAttribute('open')) return;
							this.dataset.visible = 'true';
							afterTransition(this.content, () => {
								if (this.hasAttribute('open')) this.dataset.ready = 'true';
							});
						});
					});

					trapFocus(this, this.content);
				}

				close() {
					if (!this.hasAttribute('open')) return;

					delete this.dataset.visible;
					delete this.dataset.ready;
					this.dataset.busy = 'false';
					document.body.classList.remove('overflow-hidden');
					removeTrapFocus(this.openedBy);

					if (this.quickShop && this.quickShop.dataset.state === 'open') {
						this.quickShop.dataset.state = 'idle';
					}
					this.quickShop = null;

					/* Held open until the slide-out finishes, then removed from hit testing. */
					afterTransition(this.content, () => {
						if (this.dataset.visible !== 'true') this.removeAttribute('open');
					});
				}

				showError(message) {
					if (!this.errorElement) return;
					this.errorElement.textContent = message;
					this.errorElement.hidden = false;
				}

				hideError() {
					if (!this.errorElement) return;
					this.errorElement.textContent = '';
					this.errorElement.hidden = true;
				}
			}
		);
	}

	if (!customElements.get('quick-shop')) {
		customElements.define(
			'quick-shop',
			class QuickShop extends HTMLElement {
				constructor() {
					super();

					this.trigger = this.querySelector('.quick-shop__trigger');
					this.addButton = this.querySelector('.quick-shop__add');
					this.panel = this.querySelector('.quick-shop__panel');
					this.sizesElement = this.querySelector('.quick-shop__sizes');
					this.removeButton = this.querySelector('.quick-shop__remove');
					this.errorElement = this.querySelector('.quick-shop__error');
					this.lineKey = null;

					/*
					 * The card's overlay link sits under this element, but
					 * product-card.js also binds colour-swatch handlers on the card
					 * wrapper - keep quick shop clicks to ourselves.
					 */
					this.addEventListener('click', (event) => event.stopPropagation());

					bindActivation(this.trigger, this.onTriggerClick.bind(this));
					bindActivation(this.addButton, this.onAddClick.bind(this));
					bindActivation(this.removeButton, this.onRemoveClick.bind(this));

					if (this.sizesElement) {
						bindActivation(this.sizesElement, (event) => {
							const chip = event.target.closest('.quick-shop__size');
							if (!chip || chip.disabled) return;
							this.addVariant(chip.dataset.variantId, { chip: chip });
						});
					}

					this.addEventListener('keyup', (event) => {
						if (event.code && event.code.toUpperCase() === 'ESCAPE') this.closePanel();
					});

					/* Hover-opened panel closes when the pointer leaves the card. */
					const card = this.closest('.card-wrapper');
					if (card) {
						card.addEventListener('mouseleave', () => {
							if (this.contains(document.activeElement)) return;
							this.closePanel();
						});
					}
				}

				get hasSizes() {
					return Boolean(this.sizesElement);
				}

				get sizesHTML() {
					return this.sizesElement ? this.sizesElement.innerHTML : '';
				}

				get sheet() {
					return document.querySelector('quick-shop-sheet');
				}

				onTriggerClick() {
					if (!this.hasSizes) {
						this.addVariant(this.dataset.variantId);
						return;
					}

					if (this.dataset.state === 'open') {
						this.closePanel();
						return;
					}

					this.hideError();
					this.dataset.state = 'open';
					if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');

					const firstSize = this.sizesElement.querySelector('.quick-shop__size:not([disabled])');
					if (firstSize) firstSize.focus();
				}

				onAddClick() {
					const sheet = this.sheet;
					if (!this.hasSizes || !sheet) {
						this.addVariant(this.dataset.variantId);
						return;
					}

					this.dataset.state = 'open';
					sheet.open(this);
				}

				onRemoveClick() {
					if (!this.lineKey) return;

					const cart = getCartDrawer();
					const config = fetchConfig();
					config.body = JSON.stringify({
						id: this.lineKey,
						quantity: 0,
						sections: getSectionIds(cart),
						sections_url: window.location.pathname,
					});

					this.setBusy(true);

					fetch(window.routes.cart_change_url, config)
						.then((response) => response.json())
						.then((response) => {
							if (response.status) {
								this.showError(response.description || response.message);
								return;
							}

							publish(PUB_SUB_EVENTS.cartUpdate, {
								source: 'quick-shop',
								productVariantId: this.variantId,
							});
							refreshCartSections(response.sections);

							this.lineKey = null;
							this.variantId = null;
							this.resetSizes();
							this.dataset.state = 'idle';
							if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
						})
						.catch(() => this.showError(window.cartStrings.error))
						.finally(() => this.setBusy(false));
				}

				addVariant(variantId, options) {
					if (!variantId) return;

					const settings = options || {};
					const sheet = settings.sheet || null;
					const chip = settings.chip || null;
					const cart = getCartDrawer();

					const body = new FormData();
					body.append('id', variantId);
					body.append('quantity', 1);
					body.append('sections', getSectionIds(cart).join(','));
					body.append('sections_url', window.location.pathname);

					const config = fetchConfig('javascript');
					config.headers['X-Requested-With'] = 'XMLHttpRequest';
					delete config.headers['Content-Type'];
					config.body = body;

					this.markSelected(variantId);
					if (chip) chip.setAttribute('aria-pressed', 'true');
					this.setBusy(true, sheet);
					this.hideError(sheet);

					fetch(window.routes.cart_add_url, config)
						.then((response) => response.json())
						.then((response) => {
							if (response.status) {
								this.showError(response.description || response.message, sheet);
								this.resetSizes(sheet);
								return;
							}

							/* The line key lets Remove target this exact line, not just the variant. */
							this.lineKey = response.key || variantId;
							this.variantId = String(variantId);

							/*
							 * Published before the swap, matching product-form.js. cart-drawer-items
							 * inherits a cartUpdate subscriber that refetches the cart-page section
							 * into itself; sending the event first means the element that handles it
							 * is the one the swap below is about to replace, so its late response
							 * lands on a detached node instead of the new drawer markup.
							 */
							publish(PUB_SUB_EVENTS.cartUpdate, {
								source: 'quick-shop',
								productVariantId: this.variantId,
							});
							refreshCartSections(response.sections);

							this.dataset.state = 'added';
							if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
							if (sheet) sheet.close();
						})
						.catch(() => {
							this.showError(window.cartStrings.error, sheet);
							this.resetSizes(sheet);
						})
						.finally(() => this.setBusy(false, sheet));
				}

				closePanel() {
					if (this.dataset.state !== 'open') return;

					const sheet = this.sheet;
					if (sheet && sheet.hasAttribute('open')) {
						sheet.close();
						return;
					}

					this.dataset.state = 'idle';
					if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
				}

				markSelected(variantId) {
					if (!this.sizesElement) return;
					this.sizesElement.querySelectorAll('.quick-shop__size').forEach((chip) => {
						chip.setAttribute('aria-pressed', String(chip.dataset.variantId === String(variantId)));
					});
				}

				resetSizes(sheet) {
					const containers = [this.sizesElement, sheet ? sheet.sizes : null];
					containers.forEach((container) => {
						if (!container) return;
						container.querySelectorAll('.quick-shop__size').forEach((chip) => {
							chip.setAttribute('aria-pressed', 'false');
						});
					});
				}

				setBusy(isBusy, sheet) {
					this.dataset.busy = String(Boolean(isBusy));
					if (sheet) sheet.dataset.busy = String(Boolean(isBusy));
				}

				showError(message, sheet) {
					const text = message || window.cartStrings.error;
					if (sheet) {
						sheet.showError(text);
						return;
					}
					if (!this.errorElement) return;
					this.errorElement.textContent = text;
					this.errorElement.hidden = false;
				}

				hideError(sheet) {
					if (sheet) sheet.hideError();
					if (!this.errorElement) return;
					this.errorElement.textContent = '';
					this.errorElement.hidden = true;
				}
			}
		);
	}
})();
